import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type AuthMode = "signin" | "signup";

export default function AuthPage() {
  const { t } = useTranslation();
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    if (mode === "signin") {
      const { error } = await signInWithEmail(email, password);
      if (error) setError(error.message);
    } else {
      const { error } = await signUpWithEmail(email, password);
      if (error) {
        setError(error.message);
      } else {
        setInfo(t("auth.checkEmail"));
      }
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(null);
    const { error } = await signInWithGoogle();
    if (error) setError(error.message);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "hsl(var(--bg))",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "hsl(var(--bg2))",
          borderRadius: 20,
          border: "1px solid hsl(var(--border))",
          padding: 40,
          boxShadow: "0 20px 60px rgba(0,0,0,.3)",
        }}
      >
        {/* Logo — no background wrapper; logo sits directly on card */}
        <div
          className="auth-logo-container"
          style={{
            textAlign: "center",
            marginBottom: 32,
            background: "transparent",
            backgroundColor: "transparent",
          }}
        >
          {logoError ? (
            <div className="flex items-center justify-center gap-2 mx-auto w-fit">
              <BookOpen className="w-12 h-12" style={{ color: "hsl(var(--primary))" }} />
              <span className="font-bold text-2xl" style={{ color: "hsl(var(--foreground))" }}>StudyDo</span>
            </div>
          ) : (
            <img
              src="/logo-transparent.png"
              alt="StudyDo Logo"
              className="h-24 w-auto object-contain mx-auto block"
              style={{ background: "transparent", backgroundColor: "transparent" }}
              onError={() => setLogoError(true)}
            />
          )}
          <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 4 }}>
            {t("auth.tagline")}
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            background: "hsl(var(--bg3))",
            borderRadius: 10,
            padding: 4,
            marginBottom: 24,
          }}
        >
          {(["signin", "signup"] as AuthMode[]).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(null); setInfo(null); }}
              style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                transition: "all .2s",
                background: mode === m ? "hsl(var(--bg2))" : "transparent",
                color: mode === m ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,.15)" : "none",
              }}
            >
              {m === "signin" ? t("auth.signIn") : t("auth.signUp")}
            </button>
          ))}
        </div>

        {/* Google OAuth */}
        <button
          onClick={handleGoogle}
          style={{
            width: "100%",
            padding: "11px 16px",
            borderRadius: 10,
            border: "1px solid hsl(var(--border))",
            background: "hsl(var(--bg3))",
            color: "hsl(var(--foreground))",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 20,
            transition: "all .2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "hsl(var(--bg))")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "hsl(var(--bg3))")}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          {t("auth.continueWithGoogle")}
        </button>

        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ flex: 1, height: 1, background: "hsl(var(--border))" }} />
          <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("auth.or")}</span>
          <div style={{ flex: 1, height: 1, background: "hsl(var(--border))" }} />
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 500,
                color: "hsl(var(--muted-foreground))",
                marginBottom: 6,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {t("auth.email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t("auth.emailPlaceholder")}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--bg3))",
                color: "hsl(var(--foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 14,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 500,
                color: "hsl(var(--muted-foreground))",
                marginBottom: 6,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {t("auth.password")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              minLength={6}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--bg3))",
                color: "hsl(var(--foreground))",
                fontFamily: "DM Sans, sans-serif",
                fontSize: 14,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {error && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "rgba(255,90,138,.12)",
                color: "hsl(var(--accent3))",
                fontSize: 13,
                marginBottom: 16,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {error}
            </div>
          )}

          {info && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "rgba(109,90,255,.12)",
                color: "hsl(var(--primary))",
                fontSize: 13,
                marginBottom: 16,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: 50,
              border: "none",
              background: loading
                ? "hsl(var(--muted))"
                : "hsl(var(--primary))",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all .2s",
            }}
          >
            {loading
              ? t("auth.loading")
              : mode === "signin"
              ? t("auth.signIn")
              : t("auth.createAccount")}
          </button>
        </form>
      </div>
    </div>
  );
}
